import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, KpiCard, SectionCard, StatusPill, EmptyState } from "../components/Common";
import { ClipboardList, Stethoscope, AlertTriangle, ArrowRight, ClipboardCheck, FlaskConical, FileText, Users, LineChart as LineChartIcon, X, Radio, Download, LogOut } from "lucide-react";
import { API_BASE } from "../lib/api";
import { HandoverDialog } from "./admitted/HandoverDialog";

export default function Doctor() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [queue, setQueue] = useState([]);
  const [criticalLabs, setCriticalLabs] = useState([]);
  const [unsigned, setUnsigned] = useState([]);
  const [myPatients, setMyPatients] = useState([]);
  const [drill, setDrill] = useState(null); // "critical" | "unsigned" | "rx" | "labs"
  const [rxList, setRxList] = useState([]);
  const [labList, setLabList] = useState([]);
  const [imagingMine, setImagingMine] = useState([]);
  const [showHandover, setShowHandover] = useState(false);

  const load = async () => {
    const [s,q,cl,un,mp,img] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/queue"),
      api.get("/doctor/critical-labs"),
      api.get("/doctor/unsigned-notes"),
      api.get("/doctor/my-patients"),
      api.get("/imaging/orders?mine=true"),
    ]);
    setStats(s.data); setQueue(q.data||[]); setCriticalLabs(cl.data||[]); setUnsigned(un.data||[]); setMyPatients(mp.data||[]); setImagingMine(img.data||[]);
  };
  useEffect(() => { load(); }, []);

  const openDrill = async (kind) => {
    setDrill(kind);
    if (kind==="rx") { const {data} = await api.get("/prescriptions?status=Pending"); setRxList(data||[]); }
    if (kind==="labs") { const {data} = await api.get("/lab/orders?status=Ordered"); setLabList(data||[]); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Doctor Dashboard"
        subtitle="Queue, unsigned notes, critical results and your patients."
        actions={
          <button data-testid="doc-handover-btn" onClick={() => setShowHandover(true)} className="h-10 px-4 rounded-lg text-sm font-medium border border-slate-200 hover:bg-slate-50 flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Shift handover
          </button>
        }
      />

      {showHandover && <HandoverDialog onClose={() => setShowHandover(false)} onSaved={() => setShowHandover(false)} />}

      {criticalLabs.length > 0 && (
        <div className="rounded-xl bg-[#fdecec] border border-[#f5c2c2] p-4 flex items-start gap-3" data-testid="critical-lab-alert">
          <AlertTriangle className="w-5 h-5 text-[#B71C1C] mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-[#B71C1C]">{criticalLabs.length} critical lab result{criticalLabs.length>1?"s require":" requires"} attention</div>
            <div className="mt-2 space-y-1">
              {criticalLabs.slice(0,5).map(l => (
                <button
                  key={l.order_id}
                  data-testid={`critical-link-${l.order_id}`}
                  onClick={async () => {
                    // Optimistic remove — user just acknowledged this alert
                    setCriticalLabs((prev) => prev.filter((x) => x.order_id !== l.order_id));
                    try { await api.post("/doctor/critical-labs/review", { order_id: l.order_id }); }
                    catch { /* if it fails, next load will bring it back */ }
                    navigate(`/app/patients/${l.patient_id}`);
                  }}
                  className="block text-sm underline text-[#B71C1C] hover:text-[#7f1010] text-left"
                >
                  {l.patient_name} — {l.tests.join(", ")}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <button data-testid="kpi-queue-drill" onClick={()=>openDrill("queue")} className="text-left">
          <KpiCard label="Patients waiting" value={stats.queue_waiting||0} icon={ClipboardList} tone="amber" />
        </button>
        <button data-testid="kpi-critical-drill" onClick={()=>openDrill("critical")} className="text-left">
          <KpiCard label="Critical labs" value={criticalLabs.length} icon={AlertTriangle} tone="red" hint="Click to review" />
        </button>
        <button data-testid="kpi-unsigned-drill" onClick={()=>openDrill("unsigned")} className="text-left">
          <KpiCard label="Unsigned notes" value={unsigned.length} icon={FileText} tone="blue" hint="Click to review" />
        </button>
        <button data-testid="kpi-rx-drill" onClick={()=>openDrill("rx")} className="text-left">
          <KpiCard label="Rx pending" value={stats.rx_pending||0} icon={ClipboardCheck} tone="slate" hint="Click to view" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SectionCard title="Today's queue" actions={<span className="text-xs text-slate-500">{queue.length}</span>}>
            {queue.length===0 ? <EmptyState icon={ClipboardList} title="No patients waiting" /> : (
              <ul className="divide-y divide-slate-100">
                {queue.map(q => (
                  <li key={q.queue_id} className="p-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer" onClick={()=>navigate(`/app/patients/${q.patient_id}`)}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-semibold">#{q.queue_no}</div>
                      <div><div className="text-sm font-medium">{q.patient_name}</div><div className="text-xs text-slate-500">{q.patient_ths_id} · {q.department}</div></div>
                    </div>
                    <div className="flex items-center gap-2"><StatusPill status={q.priority==="Urgent"?"critical":"normal"} /><StatusPill status={q.status} /><ArrowRight className="w-4 h-4 text-slate-400" /></div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>
        <SectionCard title="My patients (30d)">
          {myPatients.length===0 ? <EmptyState icon={Users} title="Sign notes to see your patients here" /> : (
            <ul className="divide-y divide-slate-100">
              {myPatients.slice(0,8).map(p => (
                <li key={p.patient_id} className="p-3 hover:bg-slate-50 cursor-pointer" onClick={()=>navigate(`/app/patients/${p.patient_id}`)}>
                  <div className="text-sm font-medium">{p.first_name} {p.last_name}</div>
                  <div className="text-xs text-slate-500 tabular">{p.ths_id}</div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <SectionCard title="My imaging orders" actions={<button data-testid="see-all-imaging" onClick={()=>navigate('/app/imaging')} className="text-xs text-[#1565C0] font-medium hover:underline">See all</button>}>
        {imagingMine.length===0 ? (
          <EmptyState icon={Radio} title="No imaging orders yet" description="Click 'Order imaging' to send X-Ray, CT, MRI, Ultrasound, ECG or Mammography requests to radiology." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {imagingMine.slice(0,6).map(o => (
              <li key={o.order_id} className="p-3 flex items-center justify-between hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{o.imaging_type}{o.body_part ? ` — ${o.body_part}` : ""} · {o.patient_name}</div>
                  <div className="text-xs text-slate-500">{o.order_no} · Ordered {new Date(o.created_at).toLocaleString(undefined,{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <StatusPill status={o.status === "Reported" ? "Completed" : o.status === "In Progress" ? "In Progress" : "Pending"} />
                  {o.status === "Reported" && (
                    <button data-testid={`dl-report-${o.order_id}`} onClick={async ()=>{
                      const t = localStorage.getItem("ths_session_token");
                      const res = await fetch(`${API_BASE}/imaging/orders/${o.order_id}/report`, { credentials: "include", headers: t ? {Authorization: `Bearer ${t}`} : {} });
                      if(!res.ok) return;
                      const blob = await res.blob(); const url = URL.createObjectURL(blob); window.open(url, "_blank"); setTimeout(()=>URL.revokeObjectURL(url), 60000);
                    }} className="h-7 px-2 rounded-md bg-[#e6f4ea] text-[#1B5E20] text-xs font-medium flex items-center gap-1 hover:bg-[#d3ecda]">
                      <Download className="w-3 h-3" /> Report
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {drill && (
        <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={()=>setDrill(null)}>
          <div className="bg-white rounded-2xl border border-slate-200 max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid={`drill-${drill}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">{ {critical:"Critical laboratory results", unsigned:"Unsigned SOAP notes", rx:"Pending prescriptions", labs:"New lab orders", queue:"Today's queue"}[drill] }</div>
              <button onClick={()=>setDrill(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            {drill==="critical" && (criticalLabs.length===0 ? <EmptyState title="No critical results" /> :
              <ul className="divide-y divide-slate-100">{criticalLabs.map(l => (
                <li key={l.order_id} className="p-3 hover:bg-slate-50 cursor-pointer" onClick={()=>{setDrill(null);navigate(`/app/patients/${l.patient_id}`);}}>
                  <div className="text-sm font-semibold">{l.patient_name}</div><div className="text-xs text-slate-500">{l.tests.join(", ")}</div>
                </li>
              ))}</ul>)}
            {drill==="unsigned" && (unsigned.length===0 ? <EmptyState title="Nothing unsigned" /> :
              <ul className="divide-y divide-slate-100">{unsigned.map(n => (
                <li key={n.note_id} className="p-3 hover:bg-slate-50 cursor-pointer" onClick={()=>{setDrill(null);navigate(`/app/patients/${n.patient_id}`);}}>
                  <div className="text-sm font-semibold">{n.diagnosis||"Draft note"}</div>
                  <div className="text-xs text-slate-500">{new Date(n.created_at).toLocaleString()} · {n.author_name}</div>
                </li>
              ))}</ul>)}
            {drill==="rx" && (rxList.length===0 ? <EmptyState title="No pending Rx" /> :
              <ul className="divide-y divide-slate-100">{rxList.map(r => (
                <li key={r.rx_id} className="p-3">
                  <div className="text-sm font-semibold">{r.rx_no}</div>
                  <div className="text-xs text-slate-500">{r.patient_name} · {(r.items||[]).map(i=>i.drug).join(", ")}</div>
                </li>
              ))}</ul>)}
            {drill==="queue" && (queue.length===0 ? <EmptyState title="Queue empty" /> :
              <ul className="divide-y divide-slate-100">{queue.map(q=>(
                <li key={q.queue_id} className="p-3 hover:bg-slate-50 cursor-pointer" onClick={()=>{setDrill(null);navigate(`/app/patients/${q.patient_id}`);}}>
                  <div className="text-sm font-semibold">{q.patient_name}</div><div className="text-xs text-slate-500">{q.patient_ths_id} · {q.status}</div>
                </li>
              ))}</ul>)}
          </div>
        </div>
      )}

      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600 flex items-start gap-2">
        <Stethoscope className="w-4 h-4 text-slate-500 mt-0.5" />
        <div><strong>Clinical advisory panel</strong> — order sets, drug-interaction checks, and ICD coding available on the patient chart. Not a substitute for clinical judgment.</div>
      </div>
    </div>
  );
}
